import { OptimizerRunV2State, OptimizerRunV2Type } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { TFunction } from 'i18next';
import type { RunAdvisorInsights } from '../../types/advisorInsights.types';
import type {
  OptimizerAuditRollup,
  OptimizerAuditV2Record,
} from '../../types/audit.types';
import type {
  RebalanceInsightAiContext,
  RebalanceInsightAttentionHighlight,
} from '../../types/insightNarrative.types';
import type { BulkRebalanceRun } from '../../types/run.types';
import { resolveWeightDiffValues } from '../snapshot/buildWeightDiffRows';
import { buildPortfolioAdvisorInsights } from './buildPortfolioAdvisorInsights';
import { buildRunAdvisorInsights } from './buildRunAdvisorInsights';
import { buildRunFailureReasons } from './buildRunFailureReasons';
import {
  buildCashRunSummaryForAi,
  buildTopSituationSummariesForAi,
} from './buildRunSituationSummariesForAi';
import {
  buildRunTopTickerMovesForAi,
  buildRunTopTickerRollups,
  type RunTopTickerRow,
} from './buildRunTopTickerRollups';
import { compactInsightAiContext, takeFirst } from './compactInsightAiContext';

const SITUATION_CAP = 5;
const ATTENTION_CAP = 3;
const BATCH_EXIT_CAP = 5;

export const canGenerateRunInsightNarrative = (
  run: BulkRebalanceRun,
  audits: OptimizerAuditV2Record[],
): boolean => {
  if (audits.length === 0) {
    return false;
  }

  return (
    run.state === OptimizerRunV2State.Completed ||
    run.state === OptimizerRunV2State.Failed
  );
};

const countHighTurnoverPortfolios = (
  audits: OptimizerAuditV2Record[],
  turnoverP90Pct: number,
): number =>
  audits.filter((audit) => {
    const turnoverPct = buildPortfolioAdvisorInsights(audit)?.turnoverPct;
    return turnoverPct != null && turnoverPct >= turnoverP90Pct;
  }).length;

const buildTaxableSellsHighlightReason = (
  insights: RunAdvisorInsights,
  topTaxableSells: RunTopTickerRow[],
): string | undefined => {
  const parts: string[] = [];

  if (
    insights.estRealizedGainIsComplete &&
    insights.estRealizedGainCents != null
  ) {
    parts.push('estimated gains available on passed books');
  } else if (insights.portfoliosWithIncompleteGainEstimate > 0) {
    parts.push('gain estimate incomplete on some books');
  }

  const tickers = topTaxableSells.map((row) => row.ticker);
  if (tickers.length > 0) {
    parts.push(`top taxable sells ${tickers.join(', ')}`);
  }

  return parts.length > 0 ? parts.join('; ') : undefined;
};

const aggregateBatchExitTickers = (
  audits: OptimizerAuditV2Record[],
  tickerMap: Record<string, string>,
): string[] => {
  const exitTotals = new Map<string, number>();

  for (const audit of audits) {
    if (audit.passed === false) {
      continue;
    }

    for (const subAccount of audit.subAccountResults ?? []) {
      for (const diff of subAccount.weightDiffs ?? []) {
        const financialProductId = diff.financialProductId ?? '';
        const { currentCents, proposedCents, deltaCents } =
          resolveWeightDiffValues(diff, subAccount.inputMarketValueCents ?? 0);

        if (
          !financialProductId ||
          deltaCents >= 0 ||
          currentCents <= 0 ||
          proposedCents > 0
        ) {
          continue;
        }

        const ticker = tickerMap[financialProductId]?.trim();
        if (!ticker) {
          continue;
        }

        exitTotals.set(
          ticker,
          (exitTotals.get(ticker) ?? 0) + Math.abs(deltaCents),
        );
      }
    }
  }

  return [...exitTotals.entries()]
    .sort(
      (left, right) => right[1] - left[1] || left[0].localeCompare(right[0]),
    )
    .slice(0, BATCH_EXIT_CAP)
    .map(([ticker]) => ticker);
};

export const buildRunRebalanceInsightAiContext = ({
  run,
  audits,
  rollup,
  reports = [],
  tickerMap = {},
  t,
}: {
  run: BulkRebalanceRun;
  audits: OptimizerAuditV2Record[];
  rollup?: OptimizerAuditRollup | null;
  reports?: PortfolioReport[];
  tickerMap?: Record<string, string>;
  t: TFunction;
}): RebalanceInsightAiContext => {
  const reportsById = new Map(reports.map((report) => [report.id, report]));
  const insights = buildRunAdvisorInsights(audits, reportsById);
  const topTickerRollups = buildRunTopTickerRollups(audits, tickerMap);
  const { largestSells, largestBuys } =
    buildRunTopTickerMovesForAi(topTickerRollups);
  const summary = run.portfolioSummary;
  const highTurnoverCount =
    insights?.turnoverP90Pct != null
      ? countHighTurnoverPortfolios(audits, insights.turnoverP90Pct)
      : 0;
  const failureReasons = takeFirst(
    buildRunFailureReasons(run.warnings, audits, t),
    SITUATION_CAP,
  );
  const taxableSellsReason =
    insights && insights.portfoliosWithTaxableSells > 0
      ? buildTaxableSellsHighlightReason(
          insights,
          topTickerRollups.topTaxableSells,
        )
      : undefined;
  const attentionHighlights = [
    summary?.failed && failureReasons.length === 0
      ? {
          label: t('bulkRebalance:insights.aiHighlightFailedValidation'),
          portfolioCount: summary.failed,
        }
      : null,
    insights && insights.portfoliosWithTaxableSells > 0
      ? {
          label: t('bulkRebalance:insights.aiHighlightTaxableSells'),
          portfolioCount: insights.portfoliosWithTaxableSells,
          ...(taxableSellsReason ? { reason: taxableSellsReason } : {}),
        }
      : null,
    insights?.turnoverP90Pct != null && insights.turnoverP90Pct >= 10
      ? {
          label: t('bulkRebalance:insights.aiHighlightHighTurnover'),
          portfolioCount: highTurnoverCount,
          reason: [
            insights.turnoverMedianPct != null
              ? t('bulkRebalance:insights.aiHighlightTypicalPct', {
                  value: insights.turnoverMedianPct,
                })
              : null,
            t('bulkRebalance:insights.aiHighlightHighestPct', {
              value: insights.turnoverP90Pct,
            }),
          ]
            .filter(Boolean)
            .join('; '),
        }
      : null,
  ].filter((item): item is RebalanceInsightAttentionHighlight => item != null);

  const topSituationSummaries = buildTopSituationSummariesForAi({
    situationCounts: insights?.situationCounts ?? [],
    audits,
    reports,
    t,
  });
  const cashRunSummary = buildCashRunSummaryForAi({ run, audits });
  const exits =
    run.type === OptimizerRunV2Type.Drift
      ? aggregateBatchExitTickers(audits, tickerMap)
      : [];

  return compactInsightAiContext({
    scope: 'RUN',
    runType: run.type,
    runMode: run.mode,
    portfolioCount: summary?.total ?? rollup?.portfolioCount,
    succeededCount: summary?.succeeded ?? rollup?.passedCount,
    failedCount: summary?.failed ?? rollup?.failedValidationCount,
    skippedCount: summary?.skipped,
    totalBuyCents: rollup?.totalBuyCents ?? insights?.rollup.totalBuyCents,
    totalSellCents: rollup?.totalSellCents ?? insights?.rollup.totalSellCents,
    totalTradeCount:
      rollup?.totalTradeCount ?? insights?.rollup.totalTradeCount,
    runRegisteredSellCents: insights?.registeredSellCents,
    runTaxableSellCents: insights?.taxableSellCents,
    portfoliosWithSells: insights?.portfoliosWithSells,
    portfoliosWithTaxableSells: insights?.portfoliosWithTaxableSells,
    taxShelteredSellPct: insights?.taxShelteredSellPct ?? undefined,
    turnoverMedianPct: insights?.turnoverMedianPct ?? undefined,
    turnoverP90Pct: insights?.turnoverP90Pct ?? undefined,
    topSituations: takeFirst(insights?.situationCounts, SITUATION_CAP),
    topSituationSummaries,
    cashRunSummary,
    largestSells: takeFirst(largestSells, 3),
    largestBuys: takeFirst(largestBuys, 3),
    exits: takeFirst(exits, BATCH_EXIT_CAP),
    attentionHighlights: takeFirst(attentionHighlights, ATTENTION_CAP),
    failureReasons,
  });
};
